/* =============================================================
   AccountPage — the one place to sign in, register or manage your
   account for every tool on prajnainnovates.com.

   Everyone can use every tool. An account is what lets you save:
   maps, assessment results, checklists, and sharing. Microsoft
   Entra External ID hosts the actual sign-in / sign-up screens
   (its page offers "Create one" for people without an account),
   so both buttons start the same flow; ?next= brings the person
   back to wherever they came from, including the main website.
   ============================================================ */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, startSignIn } from '../lib/auth';
import { msalReady } from '../lib/msal';
import { goTo, isExternal, safeNext, SITE_URL } from '../lib/nav';
import { AccountOverview } from '../components/AccountOverview';
import { DataSection, NewsletterSection, PlanSection, ProfileSection } from '../components/AccountSettings';

const TOOLS: { name: string; guest: string; member: string; href: string; memberHref?: string }[] = [
  {
    name: 'Interaction Map',
    guest: 'Build, import and download maps.',
    member: 'Keep your maps in your account on any device, and share them with other members to view or edit.',
    href: '/interaction-map/',
  },
  {
    name: 'Psychological Safety Assessment',
    guest: 'Take the assessment and see your results.',
    member: 'Keep your results and history, and pick up a half-finished assessment later.',
    href: '/psychological-safety-assessment',
  },
  {
    name: 'CYBHI Onboarding Checklist',
    guest: 'Pick your pathway and preview your checklist.',
    member: 'Save checklists and work through them with your team.',
    href: '/cybhi-checklist',
  },
];

/* Set once this page load has followed a fresh sign-in back to `next`, so later visits to /account stay here. */
let returnedAfterSignIn = false;

export function AccountPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { hash } = useLocation();
  const rawNext = search.get('next');
  const next = safeNext(rawNext, '/account');
  const [busy, setBusy] = useState<'in' | 'up' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  /* Right after signing in, go back to `next`, once. MSAL finishes a sign-in on the page that started it
     (navigateToLoginRequestUrl in lib/msal.ts), which is this one, so msalReady resolves here with the
     result and the `next` it carried. Opening or reloading the account page later, while signed in,
     stays here with a "Back to …" link. */
  useEffect(() => {
    let live = true;
    msalReady
      .then((r) => {
        const state = (r as { state?: string } | null | undefined)?.state;
        if (!live || returnedAfterSignIn || !state || next === '/account' || safeNext(state, '/account') !== next) return;
        returnedAfterSignIn = true;
        goTo(navigate, next);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [next, navigate]);

  async function start(kind: 'in' | 'up') {
    setErr(null);
    setBusy(kind);
    try {
      await startSignIn(next);
    } catch (e) {
      setBusy(null);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  if (auth.loading) {
    return <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">Loading…</div>;
  }

  const backLabel = rawNext && next !== '/account' ? (next.startsWith(SITE_URL) ? 'Back to the website' : 'Back to where you were') : null;

  if (auth.user) {
    const u = auth.user;
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
        <div className="eyebrow mb-4">Your account</div>
        <h1 className="editorial-h1 mb-3">
          Hello{u.user_metadata.full_name ? ', ' : ''}
          <em className="italic text-accent">{u.user_metadata.full_name ?? 'there'}.</em>
        </h1>
        <p className="editorial-deck mb-2">Signed in as {u.email}.</p>
        <p className="font-editorial text-[16px] text-ink-muted mb-12">
          Everything you've saved, across every tool, in one place.
        </p>

        <PlanSection />
        <AccountOverview />
        <NewsletterSection />
        <ProfileSection />
        <DataSection />

        <div className="flex flex-wrap gap-7 items-center">
          {backLabel && (
            isExternal(next) ? <a href={next} className="btn-text">{backLabel}</a> : <Link to={next} className="btn-text">{backLabel}</Link>
          )}
          <button type="button" onClick={() => auth.signOut()} className="btn-text">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
      <div className="eyebrow mb-4">Your account</div>
      {search.get('signedout') && (
        <p className="font-editorial italic text-[15px] text-ink-muted mb-4">You've signed out.</p>
      )}
      <h1 className="editorial-h1 mb-3">
        One account, <em className="italic text-accent">every tool.</em>
      </h1>
      <p className="editorial-deck mb-10 max-w-2xl">
        Every tool on this site is open to everyone. Sign in or create a free account to save your
        work and collaborate.
      </p>
      {hash === '#newsletter' && (
        <p className="font-editorial text-[16px] text-ink border-l-4 border-accent pl-4 mb-10 max-w-2xl">
          Here for the weekly newsletter? Sign in or create a free account, and you can sign up for it on this page.
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-3 max-w-md mb-4">
        <button
          type="button"
          onClick={() => start('in')}
          disabled={!!busy}
          className="flex-1 py-3 px-4 bg-ink text-paper text-sm font-label hover:bg-ink-soft transition-colors disabled:opacity-60"
        >
          {busy === 'in' ? 'Redirecting…' : 'Sign in'}
        </button>
        <button
          type="button"
          onClick={() => start('up')}
          disabled={!!busy}
          className="flex-1 py-3 px-4 border border-ink text-ink text-sm font-label hover:bg-paper-deep transition-colors disabled:opacity-60"
        >
          {busy === 'up' ? 'Redirecting…' : 'Create an account'}
        </button>
      </div>
      <p className="font-editorial italic text-[14px] text-ink-muted mb-12 max-w-md">
        Free. Use email and password, a one-time code, or Google. On the next screen, choose
        &ldquo;Create one&rdquo; if you don't have an account yet.
      </p>

      {err && (
        <div className="-mt-6 mb-10 font-editorial italic text-[14px] text-[#a02525] flex items-start gap-2">
          <span aria-hidden="true">⚠</span>
          <span>{err}</span>
        </div>
      )}

      <h2 className="eyebrow mb-3">What an account adds</h2>
      <div className="border border-rule overflow-x-auto">
        <table className="w-full min-w-[520px] font-editorial text-[15px] text-left">
          <thead>
            <tr className="bg-paper-deep text-ink">
              <th className="p-3 font-medium">Tool</th>
              <th className="p-3 font-medium">Without an account</th>
              <th className="p-3 font-medium">With an account</th>
            </tr>
          </thead>
          <tbody>
            {TOOLS.map((t) => (
              <tr key={t.name} className="border-t border-rule align-top">
                <td className="p-3 font-medium text-ink">
                  <a href={t.href} className="hover:text-accent">{t.name}</a>
                </td>
                <td className="p-3 text-ink-soft">{t.guest} Nothing is saved once you close the tab.</td>
                <td className="p-3 text-ink-soft">{t.member}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {backLabel && (
        <div className="mt-10">
          {isExternal(next) ? <a href={next} className="btn-text">{backLabel}</a> : <Link to={next} className="btn-text">{backLabel}</Link>}
        </div>
      )}
    </div>
  );
}
